import React, { useMemo, useState } from "react";
import { Search, Download, FileSpreadsheet, Trash2, UserCheck, CheckCircle2, X } from "lucide-react";
import { Chip, ToggleContacted, FORM_LABEL, FORM_TONE, ageBadge, fmtDate, fmtMoney } from "./_shared";
import { toCSV, downloadCSV, downloadXLSX, patchSubmission, bulkDeleteSubmissions } from "@/lib/adminApi";
import { toast } from "sonner";

const COLUMNS = [
  { label: "Received", get: (r) => new Date(r.created_at).toISOString() },
  { label: "Type", get: (r) => FORM_LABEL[r.form_type] || r.form_type },
  { label: "Name", get: (r) => r.name || "" },
  { label: "Email", get: (r) => r.email || "" },
  { label: "Phone", get: (r) => r.phone || "" },
  { label: "Organisation", get: (r) => r.organisation || "" },
  { label: "Interest", get: (r) => r.interest || "" },
  { label: "Country", get: (r) => r.country || "" },
  { label: "Pass", get: (r) => r.pass_type || "" },
  { label: "Amount", get: (r) => r.extra?.total_amount || "" },
  { label: "Currency", get: (r) => r.extra?.currency || "" },
  { label: "Payment Status", get: (r) => r.extra?.payment_status || "" },
  { label: "Assigned To", get: (r) => r.extra?.assigned_to || "" },
  { label: "Contacted", get: (r) => (r.extra?.contacted ? "yes" : "no") },
  { label: "Notes", get: (r) => r.extra?.notes || "" },
];

export default function LeadTable({
  rows,
  setRows,
  onRowClick,
  showFormType = true,
  showPayment = false,
  filename = "leads",
  filterableTypes = null,
  emptyText = "No leads yet.",
}) {
  const [q, setQ] = useState("");
  const [contacted, setContacted] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [showAssign, setShowAssign] = useState(false);
  const [assignValue, setAssignValue] = useState("");

  const assignees = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.extra?.assigned_to && s.add(r.extra.assigned_to));
    return ["all", ...Array.from(s)];
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (typeFilter !== "all" && r.form_type !== typeFilter) return false;
      if (contacted === "yes" && !r.extra?.contacted) return false;
      if (contacted === "no" && r.extra?.contacted) return false;
      if (assignee !== "all" && r.extra?.assigned_to !== assignee) return false;
      if (q) {
        const t = q.toLowerCase();
        return (
          (r.name || "").toLowerCase().includes(t) ||
          (r.email || "").toLowerCase().includes(t) ||
          (r.phone || "").toLowerCase().includes(t) ||
          (r.organisation || "").toLowerCase().includes(t)
        );
      }
      return true;
    });
  }, [rows, q, contacted, assignee, typeFilter]);

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;

  const toggleRow = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (allSelected) {
      const s = new Set(selected);
      filtered.forEach((r) => s.delete(r.id));
      setSelected(s);
    } else {
      const s = new Set(selected);
      filtered.forEach((r) => s.add(r.id));
      setSelected(s);
    }
  };

  const clearSelection = () => setSelected(new Set());

  const toggleContactedRow = async (r) => {
    try {
      const updated = await patchSubmission(r.id, { contacted: !r.extra?.contacted });
      setRows((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const exportCsv = () => {
    const source = someSelected ? filtered.filter((r) => selected.has(r.id)) : filtered;
    downloadCSV(`${filename}.csv`, toCSV(source, COLUMNS));
    toast.success(`CSV exported (${source.length} rows)`);
  };

  const exportXlsx = async () => {
    const source = someSelected ? filtered.filter((r) => selected.has(r.id)) : filtered;
    try {
      await downloadXLSX(`${filename}.xlsx`, source, COLUMNS);
      toast.success(`Excel exported (${source.length} rows)`);
    } catch (e) {
      toast.error("Excel export failed");
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected leads permanently? This cannot be undone.`)) return;
    try {
      const res = await bulkDeleteSubmissions(ids);
      setRows((prev) => prev.filter((r) => !selected.has(r.id)));
      clearSelection();
      toast.success(`Deleted ${res.deleted} leads`);
    } catch {
      toast.error("Bulk delete failed");
    }
  };

  const bulkAssign = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !assignValue.trim()) return;
    try {
      await Promise.all(ids.map((id) => patchSubmission(id, { assigned_to: assignValue.trim() })));
      // refresh rows locally
      setRows((prev) => prev.map((r) => selected.has(r.id) ? { ...r, extra: { ...(r.extra || {}), assigned_to: assignValue.trim() } } : r));
      toast.success(`Assigned ${ids.length} to ${assignValue.trim()}`);
      setShowAssign(false);
      setAssignValue("");
      clearSelection();
    } catch {
      toast.error("Bulk assign failed");
    }
  };

  const bulkContacted = async (value) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => patchSubmission(id, { contacted: value })));
      setRows((prev) => prev.map((r) => selected.has(r.id) ? { ...r, extra: { ...(r.extra || {}), contacted: value, contacted_at: value ? new Date().toISOString() : null } } : r));
      toast.success(`Marked ${ids.length} as ${value ? "contacted" : "not contacted"}`);
      clearSelection();
    } catch {
      toast.error("Bulk update failed");
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[220px] flex items-center rounded-xl border border-slate-200 bg-white focus-within:border-lime-500">
          <Search className="ml-3 h-4 w-4 text-slate-400" />
          <input
            data-testid="table-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, organisation…"
            className="w-full bg-transparent px-3 py-2.5 text-sm outline-none"
          />
        </div>
        {filterableTypes && (
          <select data-testid="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none">
            <option value="all">All types</option>
            {filterableTypes.map((t) => <option key={t} value={t}>{FORM_LABEL[t] || t}</option>)}
          </select>
        )}
        <select data-testid="filter-contacted" value={contacted} onChange={(e) => setContacted(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none">
          <option value="all">All</option>
          <option value="no">Not contacted</option>
          <option value="yes">Contacted</option>
        </select>
        <select data-testid="filter-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none">
          {assignees.map((a) => <option key={a} value={a}>{a === "all" ? "All owners" : a || "Unassigned"}</option>)}
        </select>
        <button data-testid="export-xlsx" onClick={exportXlsx} className="flex items-center gap-1.5 rounded-xl bg-lime-500 px-3 py-2.5 text-xs font-semibold text-slate-900 hover:bg-lime-400">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
        </button>
        <button data-testid="export-csv" onClick={exportCsv} className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          <Download className="h-3.5 w-3.5" /> CSV
        </button>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div data-testid="bulk-bar" className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-lime-300 bg-lime-50 px-3 py-2.5">
          <span className="text-sm font-semibold text-slate-900">{selected.size} selected</span>
          <button onClick={clearSelection} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"><X className="h-3 w-3" /> clear</button>
          <div className="mx-2 h-4 w-px bg-lime-300" />
          <button data-testid="bulk-mark-contacted" onClick={() => bulkContacted(true)} className="flex items-center gap-1 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark contacted
          </button>
          <button data-testid="bulk-assign" onClick={() => setShowAssign(true)} className="flex items-center gap-1 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <UserCheck className="h-3.5 w-3.5" /> Assign to…
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={exportXlsx} className="text-xs font-semibold text-slate-700 hover:text-slate-900">Export selection →</button>
            <button data-testid="bulk-delete" onClick={bulkDelete} className="flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700">
              <Trash2 className="h-3.5 w-3.5" /> Delete selected
            </button>
          </div>
        </div>
      )}

      {/* Assign popover */}
      {showAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={() => setShowAssign(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-lg font-bold text-slate-900">Assign {selected.size} leads</div>
            <input
              autoFocus
              placeholder="Team member name (e.g., Aadil)"
              value={assignValue}
              onChange={(e) => setAssignValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && bulkAssign()}
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-lime-500"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowAssign(false)} className="rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100">Cancel</button>
              <button onClick={bulkAssign} className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800">Assign</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-2 text-xs text-slate-500">
        Showing <span className="font-semibold text-slate-900">{filtered.length}</span> of {rows.length}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    data-testid="select-all"
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-slate-300 accent-lime-500"
                  />
                </th>
                <th className="px-4 py-3 text-left font-semibold">Received</th>
                {showFormType && <th className="px-4 py-3 text-left font-semibold">Type</th>}
                <th className="px-4 py-3 text-left font-semibold">Contact</th>
                <th className="px-4 py-3 text-left font-semibold">Organisation</th>
                {showPayment && <th className="px-4 py-3 text-left font-semibold">Amount</th>}
                <th className="px-4 py-3 text-left font-semibold">Owner</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={showFormType ? (showPayment ? 8 : 7) : (showPayment ? 7 : 6)} className="px-4 py-16 text-center text-sm text-slate-400">
                    {emptyText}
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const age = ageBadge(r.created_at);
                const isSel = selected.has(r.id);
                return (
                  <tr
                    key={r.id}
                    className={`cursor-pointer transition ${isSel ? "bg-lime-50" : "hover:bg-slate-50"}`}
                    onClick={() => onRowClick(r)}
                    data-testid={`row-${r.id}`}
                  >
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        data-testid={`select-${r.id}`}
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleRow(r.id)}
                        className="h-4 w-4 rounded border-slate-300 accent-lime-500"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-xs text-slate-500">{fmtDate(r.created_at)}</div>
                      <Chip tone={age.tone}>{age.label}</Chip>
                    </td>
                    {showFormType && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Chip tone={FORM_TONE[r.form_type] || "slate"}>{FORM_LABEL[r.form_type] || r.form_type}</Chip>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{r.name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[220px]">{r.email}</div>
                      {r.phone && <div className="text-xs text-slate-500">{r.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-800 truncate max-w-[200px]">{r.organisation || "—"}</div>
                      {r.interest && <div className="text-xs text-slate-500 truncate max-w-[200px]">{r.interest}</div>}
                    </td>
                    {showPayment && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold text-slate-900">{fmtMoney(r.extra?.total_amount, r.extra?.currency)}</div>
                        <div className="text-xs text-slate-500">{r.pass_type} • {r.extra?.delegate_type}</div>
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.extra?.assigned_to ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{r.extra.assigned_to}</span>
                      ) : (
                        <span className="text-xs text-slate-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <ToggleContacted value={!!r.extra?.contacted} onChange={() => toggleContactedRow(r)} testid={`toggle-${r.id}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
